/**
 * Visit-note DRAFT persistence — the host half of the `onSaveDraft`/
 * `initialDraft` seam on `@astrana/ui`'s `<VisitNoteDrafter>` (A7 of the
 * draft-persistence plan).
 *
 * ⚠️ BLOCKED ON TWO THINGS NOT YET TRUE OF THIS ENVIRONMENT, BOTH TRACKED:
 *
 *  1. care-management-gql PR 37723 (`saveVisitNoteDraft` mutation,
 *     `visitNoteDraft` query, admin-console migration 0023 for the backing
 *     table) is NOT merged or deployed anywhere. Every call this module makes
 *     will fail validation against today's schema. That failure is handled
 *     (see `useOnSaveVisitNoteDraft`'s fail-soft fallback below) — it must
 *     not crash or dead-end the editor — but nothing here can actually
 *     persist server-side until that PR ships and is promoted to the
 *     environment this app targets.
 *  2. The currently pinned `@astrana/ui` (see `packages/frontend/package.json`)
 *     predates `initialDraft` (astrana-ui PR #599, unmerged as of this
 *     writing): `VisitNoteDrafterProps` has no such field yet, so this file
 *     builds the READ side (`useVisitNoteDraftInitial`) ready to wire, but
 *     `NotesTab` cannot pass its result to `<VisitNoteDrafter>` until the pin
 *     is bumped past a release carrying that prop — passing it today is a
 *     `tsc` error, not a runtime one, which is the safest possible gate.
 *
 * Both are why this whole feature sits behind
 * `featureFlags.visitNoteDraftPersistence` (default OFF) — see
 * `config/featureFlags.ts`.
 *
 * GraphQL operations mirrored from `@astrana/ui` PR #599
 * (`src/graphql/mutations/visitNoteDraft.ts`), which itself was hand-written
 * against care-management-gql PR 37723's diff — NOT generated, because
 * codegen introspects the live QA schema and this operation isn't there yet.
 * RE-VERIFY FIELD NAMES AGAINST CODEGEN in both repos once PR 37723 deploys.
 */
import { useCallback } from "react";
import { gql, useMutation, useQuery, type ApolloError } from "@apollo/client";
import {
  noteDraftKey,
  writeNoteDraft,
  type NoteDocument,
  type VisitNote,
  type VisitNoteDraft,
  type VisitNoteDraftSnapshot,
} from "@astrana/ui/scribe";

export const SAVE_VISIT_NOTE_DRAFT_MUTATION = gql`
  mutation SaveVisitNoteDraft($input: SaveVisitNoteDraftInput!) {
    saveVisitNoteDraft(input: $input) {
      ehrPatientId
      sourceSystem
      author
      note
      noteDocument
      updatedAt
    }
  }
`;

export const VISIT_NOTE_DRAFT_QUERY = gql`
  query VisitNoteDraft($ehrPatientId: String!, $sourceSystem: String!) {
    visitNoteDraft(ehrPatientId: $ehrPatientId, sourceSystem: $sourceSystem) {
      ehrPatientId
      sourceSystem
      author
      note
      noteDocument
      updatedAt
    }
  }
`;

interface VisitNoteDraftGql {
  ehrPatientId: string;
  sourceSystem: string;
  author: string;
  note: Record<string, unknown>;
  noteDocument: Record<string, unknown>;
  updatedAt: string;
}

interface SaveVisitNoteDraftMutation {
  saveVisitNoteDraft: VisitNoteDraftGql;
}
interface SaveVisitNoteDraftMutationVariables {
  input: {
    ehrPatientId: string;
    sourceSystem: string;
    note: Record<string, unknown>;
    noteDocument: Record<string, unknown>;
  };
}
interface VisitNoteDraftQuery {
  visitNoteDraft: VisitNoteDraftGql | null;
}
interface VisitNoteDraftQueryVariables {
  ehrPatientId: string;
  sourceSystem: string;
}

/** ids identifying the chart a draft belongs to, as `NotesTab` has them. */
export interface DraftChartIdentity {
  memberId?: string;
  ehrPatientId?: string;
  sourceSystem?: string;
  encounterKey?: string;
}

/**
 * `onSaveDraft`-shaped callback: fires `saveVisitNoteDraft`, and on ANY
 * failure — network, or (today, always) the mutation simply not existing in
 * this environment's schema yet — keeps a copy in the SAME local
 * `sessionStorage` slot `<VisitNoteDrafter>` would have used had no
 * `onSaveDraft` been supplied at all, using the library's own exported
 * `noteDraftKey`/`writeNoteDraft` so the two never disagree on where a draft
 * for this chart lives.
 *
 * KEEPS THE LOCAL COPY, THEN RETHROWS — it does NOT swallow the error (B-1,
 * adversarial review 2026-08-19). An earlier version caught everything and
 * always resolved, which made `<VisitNoteDrafter>`'s `SaveChip` — its
 * documented "honesty seam" — LIE: every host-owned session reads
 * `onSaveDraft` having resolved as "the save reached the chart" and shows
 * "Saved <time>", whether or not it actually did. Concretely, by pin:
 *
 *   - CURRENT pin (v0.5.20): the chip's `destination` is the static
 *     `hostOwnsDrafts ? "chart" : "local"` and any resolved `onSaveDraft`
 *     marks the autosave state "saved" — so a swallowed failure showed
 *     "Saved 3:42 PM" for a draft that only ever reached this tab's
 *     `sessionStorage`. Rethrowing here instead puts the chip in its
 *     `"error"` state ("Not saved") — strictly more honest than a false
 *     "Saved", even though this pin has no fallback of its own to fall into.
 *   - POST-#599 pin (once bumped): `persistDraft`'s OWN fail-soft fallback
 *     only runs when `onSaveDraft` REJECTS — a resolved promise is taken as
 *     proof the chart save succeeded, and `saveDestination` never corrects
 *     itself. Rethrowing is what lets the library's tracked
 *     `saveDestination` state learn the save actually landed locally and
 *     show "Draft kept in this tab" — the honest wording — instead of
 *     "Saved" forever. The library then repeats the SAME local write this
 *     hook already made (same key), which is idempotent-harmless.
 *
 * The local write happens UNCONDITIONALLY before the rethrow specifically so
 * neither pin ever loses the clinician's typing while this hook's error
 * propagates — only the INDICATOR's honesty changes, not whether a copy is
 * kept.
 */
export function useOnSaveVisitNoteDraft(identity: DraftChartIdentity) {
  const { memberId, ehrPatientId, sourceSystem, encounterKey } = identity;
  const [saveMutation] = useMutation<
    SaveVisitNoteDraftMutation,
    SaveVisitNoteDraftMutationVariables
  >(SAVE_VISIT_NOTE_DRAFT_MUTATION);

  const localKey = noteDraftKey({ memberId, ehrPatientId, encounterKey });

  return useCallback(
    async (draft: VisitNoteDraft): Promise<void> => {
      const fallbackToLocal = () => {
        // Best-effort: if this ALSO fails (storage blocked/quota), there is
        // genuinely nowhere left to keep the draft — logged, not thrown
        // ITSELF (the caller below still rethrows the ORIGINAL server
        // error), so the failure reported upward is the one that actually
        // caused this, not a storage-quota detail on top of it.
        const kept = writeNoteDraft(localKey, {
          ...draft,
          savedAt: new Date().toISOString(),
        });
        if (!kept) {
          // eslint-disable-next-line no-console
          console.debug(
            "[visit-note-draft] server save failed AND the local fallback " +
              "could not keep a copy either (storage blocked or full).",
          );
        }
      };

      if (!ehrPatientId || !sourceSystem) {
        // No identity to key the server row on. Same posture as
        // `visitNoteDraft`'s own fail-closed read — never guess a key.
        fallbackToLocal();
        throw new Error(
          "Cannot save a visit-note draft without an ehrPatientId and sourceSystem.",
        );
      }

      try {
        await saveMutation({
          variables: {
            input: {
              ehrPatientId,
              sourceSystem,
              note: draft.note as unknown as Record<string, unknown>,
              noteDocument: (draft.document ?? {}) as unknown as Record<
                string,
                unknown
              >,
            },
          },
        });
      } catch (err) {
        // debug, not error/warn: this is the EXPECTED path until
        // care-management-gql PR 37723 ships, and it must not read as an
        // incident in this environment's logs.
        // eslint-disable-next-line no-console
        console.debug(
          "[visit-note-draft] saveVisitNoteDraft failed; keeping a local " +
            "(sessionStorage) copy and reporting the failure upward so the " +
            "save indicator stays honest.",
          err instanceof Error ? err.message : err,
        );
        fallbackToLocal();
        throw err;
      }
    },
    [ehrPatientId, localKey, saveMutation, sourceSystem],
  );
}

function fromGql(gqlDraft: VisitNoteDraftGql): VisitNoteDraftSnapshot {
  return {
    note: gqlDraft.note as unknown as VisitNote,
    document: gqlDraft.noteDocument as unknown as NoteDocument,
    savedAt: gqlDraft.updatedAt,
  };
}

/**
 * The READ side. Fetches the latest server-saved draft for one chart, mapped
 * onto `VisitNoteDraftSnapshot` — the shape `initialDraft` will expect once
 * the pin is bumped past astrana-ui #599.
 *
 * NOT YET WIRED INTO `<VisitNoteDrafter>` — see this file's header. Kept
 * ready so the pin-bump PR is a one-line change (pass `draft` as
 * `initialDraft`) rather than a new investigation.
 *
 * Fails closed to `undefined` on any error (schema mismatch, network) or
 * while loading — never partial/undefined-shaped data reaching the editor.
 */
export function useVisitNoteDraftInitial(
  ehrPatientId: string | undefined,
  sourceSystem: string | undefined,
): { draft: VisitNoteDraftSnapshot | undefined; error: ApolloError | undefined } {
  const { data, error } = useQuery<
    VisitNoteDraftQuery,
    VisitNoteDraftQueryVariables
  >(VISIT_NOTE_DRAFT_QUERY, {
    variables: { ehrPatientId: ehrPatientId ?? "", sourceSystem: sourceSystem ?? "" },
    skip: !ehrPatientId || !sourceSystem,
    fetchPolicy: "network-only",
    // Never let a schema-mismatch/network error propagate to an error
    // boundary — this read is advisory, and `error` below is how a caller
    // observes it (logged at debug, not surfaced to the clinician).
    errorPolicy: "all",
  });

  if (error) {
    // eslint-disable-next-line no-console
    console.debug(
      "[visit-note-draft] visitNoteDraft query failed; no host draft to " +
        "offer (local sessionStorage restore still applies).",
      error,
    );
  }

  const gqlDraft = data?.visitNoteDraft;
  return { draft: gqlDraft ? fromGql(gqlDraft) : undefined, error };
}
